<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import ParceirosAPI from 'dashboard/api/fandata/parceiros';

// Cadastro de parceiros/destinos de pendência (Bloco 2 / T-06). Admin gerencia nome, tipo
// (interna/externa) e ativo. O slug é estável (gerado no backend) — nunca editável aqui.
const { t } = useI18n();

const TIPOS = ['interna', 'externa'];

const isFetching = ref(true);
const parceiros = ref([]);

const showForm = ref(false);
const editingId = ref(null);
const savingForm = ref(false);
const form = reactive({ nome: '', tipo: 'externa', ativo: true });

const showDelete = ref(false);
const selected = ref(null);
const deleting = ref(false);

const tableHeaders = computed(() => [
  t('PARCEIROS_ADMIN.TABLE.NOME'),
  t('PARCEIROS_ADMIN.TABLE.TIPO'),
  t('PARCEIROS_ADMIN.TABLE.STATUS'),
  t('PARCEIROS_ADMIN.TABLE.ACTIONS'),
]);

const formTitle = computed(() =>
  editingId.value
    ? t('PARCEIROS_ADMIN.FORM.EDIT_TITLE')
    : t('PARCEIROS_ADMIN.FORM.NEW_TITLE')
);

const isFormValid = computed(() => form.nome.trim() && TIPOS.includes(form.tipo));

const tipoLabel = tipo => t(`PARCEIROS_ADMIN.TIPO.${tipo}`);

const loadAll = async () => {
  isFetching.value = true;
  try {
    const { data } = await ParceirosAPI.listManage();
    parceiros.value = data.parceiros || [];
  } catch (error) {
    useAlert(t('PARCEIROS_ADMIN.FORM.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const resetForm = () => {
  form.nome = '';
  form.tipo = 'externa';
  form.ativo = true;
  editingId.value = null;
};

const openNew = () => {
  resetForm();
  showForm.value = true;
};

const openEdit = parceiro => {
  editingId.value = parceiro.id;
  form.nome = parceiro.nome;
  form.tipo = parceiro.tipo;
  form.ativo = parceiro.ativo;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const saveParceiro = async () => {
  if (!isFormValid.value) return;
  savingForm.value = true;
  const payload = { nome: form.nome.trim(), tipo: form.tipo, ativo: form.ativo };
  try {
    if (editingId.value) {
      const { data } = await ParceirosAPI.update(editingId.value, payload);
      parceiros.value = parceiros.value.map(p => (p.id === data.id ? data : p));
    } else {
      const { data } = await ParceirosAPI.create(payload);
      parceiros.value = [...parceiros.value, data];
    }
    useAlert(t('PARCEIROS_ADMIN.FORM.SAVED'));
    closeForm();
  } catch (error) {
    useAlert(t('PARCEIROS_ADMIN.FORM.ERROR'));
  } finally {
    savingForm.value = false;
  }
};

const openDelete = parceiro => {
  selected.value = parceiro;
  showDelete.value = true;
};

const closeDelete = () => {
  showDelete.value = false;
  selected.value = null;
};

const confirmDelete = async () => {
  if (!selected.value) return;
  deleting.value = true;
  try {
    await ParceirosAPI.delete(selected.value.id);
    parceiros.value = parceiros.value.filter(p => p.id !== selected.value.id);
    useAlert(t('PARCEIROS_ADMIN.DELETE.SUCCESS'));
    closeDelete();
  } catch (error) {
    // Remoção guardada no backend: com histórico volta 422 pedindo pra desativar.
    const msg = error?.response?.data?.error || t('PARCEIROS_ADMIN.DELETE.ERROR');
    useAlert(msg);
    closeDelete();
  } finally {
    deleting.value = false;
  }
};

onMounted(loadAll);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('PARCEIROS_ADMIN.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PARCEIROS_ADMIN.HEADER')"
        :description="$t('PARCEIROS_ADMIN.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4 mt-4">
        <div class="flex items-start justify-between gap-4">
          <p class="text-sm text-n-slate-11 max-w-2xl">
            {{ $t('PARCEIROS_ADMIN.HINT') }}
          </p>
          <Button
            :label="$t('PARCEIROS_ADMIN.ADD')"
            icon="i-lucide-plus"
            size="sm"
            class="flex-shrink-0"
            @click="openNew"
          />
        </div>

        <BaseTable
          :headers="tableHeaders"
          :items="parceiros"
          :no-data-message="$t('PARCEIROS_ADMIN.EMPTY')"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="p in items" :key="p.id" :item="p">
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12 capitalize">
                    {{ p.nome }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ tipoLabel(p.tipo) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                    :class="
                      p.ativo
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    {{
                      p.ativo
                        ? $t('PARCEIROS_ADMIN.ATIVO')
                        : $t('PARCEIROS_ADMIN.INATIVO')
                    }}
                  </span>
                </BaseTableCell>
                <BaseTableCell align="end">
                  <div class="flex gap-3 justify-end flex-shrink-0">
                    <Button icon="i-woot-edit-pen" slate sm @click="openEdit(p)" />
                    <Button
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      @click="openDelete(p)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
    </template>

    <!-- Form de criar/editar -->
    <woot-modal v-model:show="showForm" :on-close="closeForm">
      <div class="flex flex-col gap-4 p-8">
        <h2 class="text-lg font-medium text-n-slate-12">{{ formTitle }}</h2>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('PARCEIROS_ADMIN.FORM.NOME_LABEL') }}
          </span>
          <input
            v-model="form.nome"
            type="text"
            :placeholder="$t('PARCEIROS_ADMIN.FORM.NOME_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>
        <div class="flex items-center gap-4">
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('PARCEIROS_ADMIN.FORM.TIPO_LABEL') }}
            </span>
            <select
              v-model="form.tipo"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option v-for="ti in TIPOS" :key="ti" :value="ti">
                {{ tipoLabel(ti) }}
              </option>
            </select>
          </label>
          <label class="flex items-center gap-2 mt-6">
            <input v-model="form.ativo" type="checkbox" />
            <span class="text-sm text-n-slate-12">
              {{ $t('PARCEIROS_ADMIN.FORM.ATIVO_LABEL') }}
            </span>
          </label>
        </div>
        <p v-if="editingId" class="text-xs text-n-slate-10 -mt-1">
          {{ $t('PARCEIROS_ADMIN.FORM.SLUG_HINT') }}
        </p>
        <div class="flex justify-end gap-3 mt-2">
          <Button
            :label="$t('PARCEIROS_ADMIN.FORM.CANCEL')"
            variant="faded"
            color="slate"
            size="sm"
            @click="closeForm"
          />
          <Button
            :label="$t('PARCEIROS_ADMIN.FORM.SAVE')"
            size="sm"
            :disabled="!isFormValid"
            :is-loading="savingForm"
            @click="saveParceiro"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDelete"
      :on-close="closeDelete"
      :on-confirm="confirmDelete"
      :title="$t('PARCEIROS_ADMIN.DELETE.CONFIRM')"
      :message="selected ? selected.nome : ''"
      :confirm-text="$t('PARCEIROS_ADMIN.DELETE.YES')"
      :reject-text="$t('PARCEIROS_ADMIN.DELETE.NO')"
    />
  </SettingsLayout>
</template>
